/* eslint-disable max-len */
import {
    GridItem, SimpleGrid, Image,
    Flex,
    Text,
    Box,
    Heading,
    List,
    ListItem,
    Link,
    VStack,
    Button
} from '@chakra-ui/react';
import { t } from '@lingui/macro';
import React from 'react';

import { useSelectorSignupForFreeTrialUrl } from '../../../../../configuration';
import { useSelectorSchool } from '../../../../../user-profiles';
import { NewOverlayContentBackground } from '../../product-licence-manager.styled';
import home from './assets/Home.png';
import listIcon from './assets/listIcon.svg';
import student from './assets/student.svg';
import teacher from './assets/teacher.svg';

type ListItemInfoProps = {
    text: string
};

type NewLicenceErrorOverlayProps = {
    licenseStatus: string
};

export const ListItemInfo = ({ text } : ListItemInfoProps) => {
    return (
        <Flex m={'24px'} flexDirection={'row'}>
            <Image src={listIcon} w={'30px'} h={'30px'} fill={'green'} mr={'10px'} />
            <ListItem>
                {text}
            </ListItem>
        </Flex>
    );
};

type GridItemInfoProps = {
    infoLines: string[]
    type: string
    isFullLicenceExpired: boolean
};

export const GridItemInfo = ({
    infoLines, type, isFullLicenceExpired
} : GridItemInfoProps) => {
    return (
        <GridItem textAlign={'left'} m={isFullLicenceExpired ? '' : '2% 6%'}>
            <Heading fontSize={'44px'} fontWeight={'400'} textAlign={isFullLicenceExpired ? 'left' : 'center'}>
                {type === 'student' ? t`mathletics.results.heading` : t`mathletics.teachers.heading`}
            </Heading>
            <Flex
                border={isFullLicenceExpired ? '2px solid #F7D064' : '0'}
                boxShadow={isFullLicenceExpired ? '0' : '0px 2px 20px 0px rgba(0, 25, 55, 0.08)'}
                bg={'white'}
                borderRadius={'24px'}
                m={'20px 0'}
                fontSize={'20px'}
                justifyContent={'space-between'}
            >
                {type === 'teacher' && <Image src={teacher} />}
                <List alignContent={'center'}>
                    {infoLines.map((info, i)=> <ListItemInfo key={i} text={info} />)}
                </List>
                {type === 'student' && <Image src={student} />}
            </Flex>
        </GridItem>
    );
};

export const GridItemExpiryMessage = (() => {
    const customerEmail = t`expiry.message.email`;
    const school = useSelectorSchool();
    const schoolName = school?.name;

    return (
        <GridItem m={'2% 6%'}>
            <Flex justifyContent={'space-between'}>
                <Image src={home} width={'436px'} height={'300px'} alignSelf={'center'} />
                <VStack padding={'10px 40px'} textAlign={'left'} alignItems={'flex-start'}>
                    <Box>
                        <Text fontSize={'32px'} >
                            {t`expiry.message.line1`}
                        </Text>
                        <Text fontSize={'32px'}>
                            {t`expiry.message.line2`}
                        </Text>
                    </Box>
                    <Box>
                        <Text fontSize={'24px'} marginTop={'30px'}>
                            {t`expiry.message.line3`}
                        </Text>
                        <Link color={'#007ACC'} href={`mailto:${customerEmail}?Subject=${t`customerServiceEmail.licenseExpiry`}&body=${schoolName}`} isExternal>
                            <Text fontSize={'24px'} fontWeight={'700'}>
                                {customerEmail}
                            </Text>
                        </Link>
                    </Box>
                </VStack>
            </Flex>
        </GridItem>
    );
});

export const GridItemNoFullLicenceMessage = (() => {
    const customerEmail = t`expiry.message.email`;
    const signUpUrl = useSelectorSignupForFreeTrialUrl();
    const school = useSelectorSchool();
    const schoolName = school?.name;

    return (
        <GridItem m={'2% 6%'}>
            <Flex justifyContent={'space-between'}>
                <VStack textAlign={'left'} alignItems={'flex-start'}>
                    <Box>
                        <Text fontSize={'32px'} >
                            {t`expiry.message.nolicence`}
                        </Text>
                        <Button
                            variant={'primary'}
                            padding={'8px 32px'}
                            borderRadius={'40px'}
                            marginTop={'32px'}
                            onClick={() => window.open(signUpUrl)}
                        >
                            {t`expiry.button.signup`}
                        </Button>
                    </Box>
                    <Box>
                        <Text fontSize={'24px'} marginTop={'30px'}>
                            {t`expiry.message.line3`}&nbsp;
                            <Link color={'#007ACC'} href={`mailto:${customerEmail}?Subject=${t`customerServiceEmail.noActiveLicense`}&body=${schoolName}`} fontSize={'24px'} fontWeight={'700'} isExternal>
                                {customerEmail}
                            </Link>
                        </Text>
                    </Box>
                </VStack>
            </Flex>
        </GridItem>
    );
});

export const NewLicenceErrorOverlay = ({ licenseStatus }: NewLicenceErrorOverlayProps) => {
    const resultsInfo = [
        t`mathletics.results.info1`,
        t`mathletics.results.info2`,
        t`mathletics.results.info3`,
        t`mathletics.results.info4`,
        t`mathletics.results.info5`
    ];
    const teachersInfo = [
        t`mathletics.teachers.info1`,
        t`mathletics.teachers.info2`,
        t`mathletics.teachers.info3`,
        t`mathletics.teachers.info4`,
        t`mathletics.teachers.info5`
    ];
    const isFullLicenceExpired = licenseStatus !== '';

    return (
        <NewOverlayContentBackground data-testid={'new-licence-error-overlay'}>
            <SimpleGrid overflowY={'scroll'} rowGap={'24px'}>
                {isFullLicenceExpired ? <GridItemExpiryMessage /> : <GridItemNoFullLicenceMessage />}
                <SimpleGrid rowGap={'24px'} bg={isFullLicenceExpired ? 'transparent' : 'white'} m={isFullLicenceExpired ? '2% 6%' : 0}>
                    <GridItemInfo infoLines={resultsInfo} type={'student'} isFullLicenceExpired={isFullLicenceExpired} />
                    <GridItemInfo infoLines={teachersInfo} type={'teacher'} isFullLicenceExpired={isFullLicenceExpired} />
                </SimpleGrid>
            </SimpleGrid>
        </NewOverlayContentBackground>
    );
};

